import { onUnmounted, type Ref, watch } from "vue"

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(", ")

/**
 * Keep keyboard focus inside a dialog while it is open and restore it to the
 * previously focused element when it closes.
 *
 * - Tab / Shift+Tab wrap within the container instead of escaping to the page.
 * - Escape (optional) invokes `onEscape` so the caller can close the dialog.
 * - The element focused before activation is re-focused on deactivation.
 *
 * The composable does NOT move focus into the dialog by itself — callers keep
 * their own "focus first field / focus panel" behavior on open.
 */
export function useFocusTrap(
  container: Ref<HTMLElement | null | undefined>,
  active: Ref<boolean>,
  options: { onEscape?: () => void } = {},
) {
  let lastFocusedEl: HTMLElement | null = null

  function getFocusable(): HTMLElement[] {
    const root = container.value
    if (!root) return []
    return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
      (el) => el.offsetParent !== null || el === document.activeElement,
    )
  }

  function handleKeydown(event: KeyboardEvent) {
    if (!active.value) return
    if (event.key === "Escape") {
      if (options.onEscape) {
        event.stopPropagation()
        options.onEscape()
      }
      return
    }
    if (event.key !== "Tab") return
    const root = container.value
    if (!root) return

    const focusable = getFocusable()
    if (focusable.length === 0) {
      // Keep focus pinned on the (tabindex="-1") panel itself.
      event.preventDefault()
      root.focus?.()
      return
    }

    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    const current = document.activeElement as HTMLElement | null
    const inside = current ? root.contains(current) : false

    if (event.shiftKey) {
      if (!inside || current === first) {
        event.preventDefault()
        last.focus()
      }
    } else if (!inside || current === last) {
      event.preventDefault()
      first.focus()
    }
  }

  function activate() {
    lastFocusedEl = document.activeElement as HTMLElement | null
    document.addEventListener("keydown", handleKeydown, true)
  }

  function deactivate() {
    document.removeEventListener("keydown", handleKeydown, true)
    const el = lastFocusedEl
    lastFocusedEl = null
    // Restore focus to whatever opened the dialog so keyboard users don't
    // lose their place. Guarded — the opener may have been unmounted.
    if (el?.isConnected) el.focus?.()
  }

  watch(
    active,
    (isActive, wasActive) => {
      if (isActive && !wasActive) activate()
      else if (!isActive && wasActive) deactivate()
    },
    { immediate: false },
  )

  // If the dialog was open on activation of the composable (rare), or the
  // component unmounts while open, make sure the listener is removed.
  if (active.value) activate()
  onUnmounted(() => {
    document.removeEventListener("keydown", handleKeydown, true)
  })
}
